import { CreateSubscriptionState, ReportPeriodState } from '@urc-ui/store/slices/create-subscription/createSubscriptionSlice';
import { FacetEnum } from '@urc-ui/store/api/report-metadata/types';
import {
  CreateSubscriptionRequest,
  EditSubscriptionRequest,
  GetReportsRequest,
  ListSubscriptionsRequest
} from '@urc-ui/store/api/ross/types/api-types';
import { Delivery, FacetOption, Filter, Pagination, ReportPeriod, Schedule, Sort as RossSort } from '@urc-ui/store/api/ross/types/types';
import {
  ACCOUNTS_FILTER_ADVERTISER_ID_VALUE_KEY,
  ACCOUNTS_FILTER_KEY,
  CAMPAIGN_SITE_FILTER_ID,
  CAMPAIGN_SITE_FILTER_VALUE_AMAZON_BUSINESS,
  MARKETPLACE_FILTER_KEY,
  MARKETPLACE_IDS_FILTER_ID,
  MRC_ACCREDITATION_STATUS_FILTER_ID,
  MRC_ACCREDITATION_STATUS_FILTER_VALUE_SIVT,
  REPORT_CATEGORY_KEY,
  REPORT_NAME_FILTER_KEY,
  REPORT_NAME_FILTER_VALUE_KEY,
  REPORT_STATUS_FILTER_KEY,
  REPORT_STATUS_FILTER_VALUE_KEY
} from '@urc-ui/store/api/ross/constants/filter-constants';
import { PRESET_PERIOD, REPORT_CATEGORY, SUBSCRIPTION_FREQUENCY } from '@urc-ui/utils/types';
import { logger } from '@urc-ui/services/logger';
import { SubscriptionDelivery } from '@urc-ui/store/api/subscriptions/types';
import { SCHEDULE_FREQUENCY, SCHEDULE_TYPE } from '@urc-ui/store/api/ross/constants/schedule-constants';
import dateTimeFns from '@urc-ui/components/date-input/utils/date-time-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { RECURRING_SCHEDULE_FREQUENCY_MAP, SORT_COLUMN_MAP, SORT_DIRECTION_MAP } from '@urc-ui/store/api/ross/constants/mappings';
import { Sort } from '@urc-ui/store/api/types';
import { VirtualColId } from '@urc-ui/pages/landing/subscriptions-table/constants';
import { COLUMN_ID } from '@urc-ui/pages/landing/subscriptions-table/column-configurations';
import { DEFAULT_ROSS_SORT_CONFIG } from '@urc-ui/store/api/ross/constants/sort-constants';

export const buildCreateSubscriptionRequest = (
  createSubscriptionPayload: CreateSubscriptionState,
  timezone: string
): CreateSubscriptionRequest => {
  if (
    !createSubscriptionPayload ||
    !createSubscriptionPayload.reportCategory ||
    !createSubscriptionPayload.reportType ||
    !createSubscriptionPayload.reportName ||
    !createSubscriptionPayload.timeUnit ||
    !createSubscriptionPayload.subscriptionDelivery ||
    !createSubscriptionPayload.reportPeriod
  ) {
    const errorMessage = 'invalid create report data';
    logger.logFatalMessage(errorMessage, createSubscriptionPayload);
    throw new Error(errorMessage);
  }

  return {
    columns: [],
    delivery: buildReportDelivery(createSubscriptionPayload.recipients),
    facetOptions: buildFacetOptions(createSubscriptionPayload),
    filters: buildFilters(createSubscriptionPayload),
    reportCategory: createSubscriptionPayload.reportCategory,
    reportName: createSubscriptionPayload.reportName,
    reportPeriod: buildReportPeriod(createSubscriptionPayload.reportPeriod, timezone),
    reportType: createSubscriptionPayload.reportType,
    schedule: buildSchedule(createSubscriptionPayload.subscriptionDelivery, timezone)
  };
};

export const buildReportDelivery = (recipients: string[] | null): Delivery => {
  return {
    email: recipients === null ? null : recipients
  };
};

export const buildFacetOptions = (createSubscriptionPayload: CreateSubscriptionState): FacetOption[] => {
  return [
    ...buildTimeunitFacet(createSubscriptionPayload.timeUnit || ''),
    ...buildMTAFacet(createSubscriptionPayload.mtaPickerChecked || false),
    ...buildCOVFacet(createSubscriptionPayload.covPickerChecked || false, createSubscriptionPayload.currencyOfVisualization || null)
  ];
};

const buildTimeunitFacet = (timeUnit: string): FacetOption[] => {
  return [
    {
      id: FacetEnum.TIME_UNIT,
      selection: [timeUnit]
    }
  ];
};

const buildMTAFacet = (mtaPickerChecked: boolean): FacetOption[] => {
  if (mtaPickerChecked) {
    return [
      {
        id: FacetEnum.MTA,
        selection: [FacetEnum.MTA]
      }
    ];
  }
  return [];
};

const buildCOVFacet = (covPickerChecked: boolean, cov: string | null) => {
  if (covPickerChecked && !!cov) {
    return [
      {
        id: FacetEnum.COV,
        selection: [cov]
      }
    ];
  }
  return [];
};

export const buildFilters = (createSubscriptionPayload: CreateSubscriptionState): Filter[] => {
  return [
    ...getCampaignSiteFilter(createSubscriptionPayload),
    ...getMarketplaceIdsFilter(createSubscriptionPayload),
    ...getMrcAccreditationStatusFilter(createSubscriptionPayload)
  ];
};

const getMrcAccreditationStatusFilter = (createSubscriptionPayload: CreateSubscriptionState): Filter[] => {
  if (createSubscriptionPayload.mrcPickerChecked === true) {
    return [
      {
        id: MRC_ACCREDITATION_STATUS_FILTER_ID,
        values: [{ [MRC_ACCREDITATION_STATUS_FILTER_ID]: MRC_ACCREDITATION_STATUS_FILTER_VALUE_SIVT }]
      }
    ];
  }
  return [];
};

const getCampaignSiteFilter = (createSubscriptionPayload: CreateSubscriptionState): Filter[] => {
  if (createSubscriptionPayload.amazonBusinessPickerChecked === true) {
    return [
      {
        id: CAMPAIGN_SITE_FILTER_ID,
        values: [{ [CAMPAIGN_SITE_FILTER_ID]: CAMPAIGN_SITE_FILTER_VALUE_AMAZON_BUSINESS }]
      }
    ];
  }
  return [];
};

const getMarketplaceIdsFilter = (createSubscriptionPayload: CreateSubscriptionState): Filter[] => {
  if (createSubscriptionPayload.marketplaceIds && createSubscriptionPayload.marketplaceIds.length > 0) {
    return [
      {
        id: MARKETPLACE_IDS_FILTER_ID,
        values: createSubscriptionPayload.marketplaceIds?.map(marketplace => {
          return { [MARKETPLACE_FILTER_KEY]: marketplace };
        })
      }
    ];
  }
  return [];
};

export const buildReportPeriod = (reportPeriod: ReportPeriodState, timezone: string): ReportPeriod => {
  if (PRESET_PERIOD.CUSTOM === reportPeriod?.period) {
    return {
      startDate: reportPeriod.start,
      endDate: reportPeriod.end
    };
  }
  return {
    preset: reportPeriod.period,
    timezone: timezone
  };
};

export const buildSchedule = (delivery: SubscriptionDelivery, timezone: string): Schedule => {
  if (delivery.frequency === SUBSCRIPTION_FREQUENCY.SINGLE) {
    return buildOneTimeSchedule(delivery, timezone);
  }
  return buildRecurringSchedule(delivery, timezone);
};

const buildOneTimeSchedule = (delivery: SubscriptionDelivery, timezone: string): Schedule => {
  if (delivery.deliveryMonth && delivery.deliveryDate && delivery.deliveryYear) {
    return buildOneTimeFutureSchedule(delivery, timezone);
  }
  return buildOneTimeNowSchedule();
};

const buildOneTimeNowSchedule = (): Schedule => {
  return {
    type: SCHEDULE_TYPE.NOW,
    frequency: SCHEDULE_FREQUENCY.ONE_TIME
  };
};

const buildOneTimeFutureSchedule = (delivery: SubscriptionDelivery, timezone: string): Schedule => {
  const year = `${delivery.deliveryYear}`.padStart(4, '0');
  // This function is never invoked with null delivery Month
  const month = dateTimeFns.getMonthNumber(delivery.deliveryMonth ?? 'JANUARY');
  const date = `${delivery.deliveryDate}`.padStart(2, '0');
  const hour = `${delivery.hour ?? '00'}`.padStart(2, '0');
  const minute = `${delivery.minute ?? '00'}`.padStart(2, '0');

  const runDate = `${year}-${month}-${date}T${hour}:${minute}:00`;

  return {
    type: SCHEDULE_TYPE.FUTURE,
    frequency: SCHEDULE_FREQUENCY.ONE_TIME,
    runDate: runDate,
    timezone: timezone
  };
};

// Builds a recurring Schedule. The hour of day and day Of month values are computed based on the current timestamp in user timezone.
const buildRecurringSchedule = (delivery: SubscriptionDelivery, timezone: string): Schedule => {
  if (!delivery.scheduledEndDate) {
    const errorMessage = 'Invalid Expiration Timestamp on Recurring Schedule';
    logger.logFatalMessage(errorMessage, delivery);
    throw new Error(errorMessage);
  }
  const now = new Date();
  const expiration = formatInTimeZone(delivery.scheduledEndDate, timezone, "yyyy-MM-dd'T'HH:mm:ss");

  return {
    type: SCHEDULE_TYPE.RECURRING,
    frequency: RECURRING_SCHEDULE_FREQUENCY_MAP[delivery.frequency] ?? delivery.frequency,
    daysOfWeek: delivery.deliveryDays && delivery.deliveryDays.length > 0 ? delivery.deliveryDays : null,
    expiration: expiration.toString(),
    dayOfMonth: delivery.frequency === SUBSCRIPTION_FREQUENCY.MONTHLY_DATE ? now.getDate() : null,
    hourOfDay: now.getHours(),
    timezone: timezone
  };
};

export const buildListSubscriptionsRequest = (
  pageSize: number,
  filterModel?: { [key: string]: any },
  sortModel?: Sort,
  nextToken?: string
): ListSubscriptionsRequest => {
  return {
    filters: buildListSubscriptionFilters(filterModel),
    sort: sortModel ? buildListSubscriptionSort(sortModel) : DEFAULT_ROSS_SORT_CONFIG,
    pagination: buildListSubscriptionPagination(pageSize, nextToken)
  };
};

const buildListSubscriptionFilters = (filterModel?: { [key: string]: any }): Filter[] => {
  const filters: Filter[] = [];
  const appContext = window.AppContext;

  // Account Filter to filter for specific logged in Marketplace
  filters.push({
    id: ACCOUNTS_FILTER_KEY,
    values: [
      {
        [ACCOUNTS_FILTER_ADVERTISER_ID_VALUE_KEY]: appContext.merchantId as string,
        [MARKETPLACE_FILTER_KEY]: appContext.marketplaceId as string
      }
    ]
  });
  const filterGroup = filterModel ? (filterModel as Record<string, any>) : {};
  if (filterModel) {
    Object.keys(filterGroup).forEach(filterType => {
      if (filterType === VirtualColId.QuickFilterVirtualColumnId) {
        filters.push(getSubscriptionNameFilter(filterModel[filterType].filter));
      } else if (filterType === COLUMN_ID.STATUS) {
        filters.push(getStatusColumnFilter(filterModel[filterType].values as string[]));
      } else if (filterType === COLUMN_ID.REPORT_CATEGORY) {
        filters.push(getCategoryColumnFilter(filterModel[filterType].values as string[]));
      }
    });
  }
  return filters;
};

const getSubscriptionNameFilter = (subscriptionName: string): Filter => {
  return {
    id: REPORT_NAME_FILTER_KEY,
    values: [{ [REPORT_NAME_FILTER_VALUE_KEY]: subscriptionName }]
  };
};

const getStatusColumnFilter = (values: string[]): Filter => {
  return {
    id: REPORT_STATUS_FILTER_KEY,
    values: values.map(value => {
      return { [REPORT_STATUS_FILTER_VALUE_KEY]: value };
    })
  };
};

const getCategoryColumnFilter = (values: string[]): Filter => {
  const includedCategories: Record<string, string>[] = [];
  values.forEach(category => {
    if (category === REPORT_CATEGORY.HSA) {
      includedCategories.push({ [REPORT_CATEGORY_KEY]: category });
      includedCategories.push({ [REPORT_CATEGORY_KEY]: REPORT_CATEGORY.SB });
      includedCategories.push({ [REPORT_CATEGORY_KEY]: REPORT_CATEGORY.SBV });
    } else if (category === REPORT_CATEGORY.SD) {
      includedCategories.push({ [REPORT_CATEGORY_KEY]: category });
      includedCategories.push({ [REPORT_CATEGORY_KEY]: REPORT_CATEGORY.PDA });
    } else {
      includedCategories.push({ [REPORT_CATEGORY_KEY]: category });
    }
  });

  return {
    id: REPORT_CATEGORY_KEY,
    values: includedCategories
  };
};

const buildListSubscriptionSort = (sort: Sort): RossSort => {
  return {
    column: SORT_COLUMN_MAP[sort.columnName],
    direction: SORT_DIRECTION_MAP[sort.order]
  };
};

const buildListSubscriptionPagination = (pageSize: number, nextToken?: string): Pagination => {
  return {
    pageSize,
    nextToken
  };
};

export const buildGetReportsRequest = (pageSize: number, subscriptionId: string, nextToken?: string): GetReportsRequest => {
  return {
    pagination: { pageSize, nextToken, paginateForward: nextToken ? true : undefined },
    subscriptionId: subscriptionId
  };
};

export interface EditSubscriptionParams {
  id: string;
  timezone: string;
  reportName?: string;
  subscriptionDelivery?: SubscriptionDelivery;
  recipients?: string[];
  status?: string;
}

export const buildEditSubscriptionRequest = ({
  id,
  timezone,
  reportName,
  subscriptionDelivery,
  recipients,
  status
}: EditSubscriptionParams): EditSubscriptionRequest => {
  return {
    subscriptions: [
      {
        id,
        name: reportName,
        status,
        deliverySettings: buildReportDelivery(recipients || null),
        schedule: subscriptionDelivery ? buildSchedule(subscriptionDelivery, timezone) : undefined
      }
    ]
  };
};
